/**
 * The status line's one clock (owner 2026-09-29, Tradovate reference 9; owner 2026-10-04 UI
 * clean-up: the only clock on the desk): a live "7:48:15 AM CDT". The account summary that lived
 * here moved to the position strip (PositionStrip.tsx; balance to the Accounts module).
 *
 * Clock (Settings → Top-bar clock): exchange time (US Central, Tradovate's "Show Time In:
 * Central") or the display time zone; "auto" (default) is exchange time in every style. Clicking
 * it switches between the two for the session (a view toggle, not a setting).
 */
import { useEffect, useState } from "react";
import { EXCHANGE_TIME_ZONE, topBarClockOf, useSettings, zoneAbbreviation } from "@helm/core";
import { useDisplayZone } from "../time/useDisplayZone";
import "./tradovateTopBar.css";

function useSecondTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Align to the next whole second so the display never skips or repeats a second.
    let t: ReturnType<typeof setTimeout>;
    const tick = (): void => {
      setNow(Date.now());
      t = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    };
    t = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    return () => {
      clearTimeout(t);
    };
  }, []);
  return now;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function clockText(ms: number, zone: string): string {
  let f = fmtCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    fmtCache.set(zone, f);
  }
  return f.format(ms);
}

/** The live top-bar clock; null when Settings → Top-bar clock resolves to off. */
export function TopBarClock(): React.JSX.Element | null {
  const mode = useSettings((s) => topBarClockOf(s.settings));
  const display = useDisplayZone();
  const [flip, setFlip] = useState(false);
  const now = useSecondTick();
  if (mode === null) return null;
  const base = mode === "exchange" ? EXCHANGE_TIME_ZONE : display;
  const other = mode === "exchange" ? display : EXCHANGE_TIME_ZONE;
  const zone = flip ? other : base;
  return (
    <button
      type="button"
      className="topbar-clock num"
      title={`${zone} · click to show ${zoneAbbreviation(flip ? base : other, now)}`}
      aria-label={`Clock, ${zoneAbbreviation(zone, now)}`}
      onClick={() => {
        setFlip((f) => !f);
      }}
    >
      {clockText(now, zone)} <span className="topbar-clock-zone">{zoneAbbreviation(zone, now)}</span>
    </button>
  );
}
